import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faBars,
  faChevronDown,
  faEye,
  faBan,
  faChevronLeft,
  faChevronRight,
  faCheck,
  faBuilding,
  faExclamationTriangle,
  faMapMarkerAlt,
  faBell,
  faMobileAlt,
  faLocationArrow,
  faSort,
  faSortUp,
  faSortDown,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase, supabaseAdmin } from '../lib/supabaseClient';
import DriverModal from '../components/DriverModal';
import AssignCompanyModal from '../components/AssignCompanyModal';
import DriverNotificationModal from '../components/DriverNotificationModal';
import { encryptPhoneNumber } from '../utils/phoneEncrypt';

function Drivers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false); // Add page loading state
  const [error, setError] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    vehicleType: '',
    status: 'approved',
    company: '',
    towPlate: '',
    trailerPlate: '',
    birthDateFrom: '',
    birthDateTo: '',
    loadStatus: '',
    locationStatus: '',
  });
  const [companies, setCompanies] = useState([]);
  const [locationSharings, setLocationSharings] = useState([]);
  const [trackingRecord, setTrackingRecord] = useState(null);
  const [filteredDrivers, setFilteredDrivers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [driversPerPage, setDriversPerPage] = useState(5); // Number of drivers per page
  const [totalDrivers, setTotalDrivers] = useState(0);
  const [totalFilteredDrivers, setTotalFilteredDrivers] = useState(0); // Add total filtered count
  const [isAssignCompanyModalOpen, setIsAssignCompanyModalOpen] =
    useState(false);
  const [selectedDriverForCompany, setSelectedDriverForCompany] =
    useState(null);
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [driverLocations, setDriverLocations] = useState({});
  const [driverLoadStatus, setDriverLoadStatus] = useState({});
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [selectedDriverForNotification, setSelectedDriverForNotification] =
    useState(null);
  const [sortOrder, setSortOrder] = useState('asc');

  // Fetch drivers from Supabase
  useEffect(() => {
    // If you only need the last driver, use fetchLastDriver() instead
    // fetchLastDriver();

    fetchDrivers();
    fetchCompanies();
    fetchLocationSharings();

  }, []);

  const toggleSortOrder = () => {
    setSortOrder(prevOrder => prevOrder === 'asc' ? 'desc' : 'asc');
  };

  // Fetch companies from Supabase
  const fetchCompanies = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, company_name')
        .eq('user_type', 'company')
        .order('company_name', { ascending: true });

      if (error) throw error;
      setCompanies(data || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchLocationSharings = async () => {
    try {
      const now = new Date().toISOString();

      const { data, error } = await supabase
        .from('share_location')
        .select('record_id, driver_id')
        .eq('status', 'TRUE')
        .gt('end_date', now);

      if (error) throw error;
      setLocationSharings(data);

    } catch (error) {
      console.error('Error fetching location sharings:', error);
    }
  };

  const isSharingLocation = (userId) => {
    return locationSharings.some(item => item.driver_id === userId);
  }

  const getTrackingRecord = (userId) => {
    const record = locationSharings.find(item => item.driver_id === userId);
    console.log(record);

    return record ? record.record_id : null;
  };

  // Check if location is older than 6 hours
  const isLocationOld = (timestamp) => {
    if (!timestamp) return true;

    const locationTime = new Date(timestamp);
    const currentTime = new Date();
    const sixHoursAgo = new Date(currentTime.getTime() - 6 * 60 * 60 * 1000);

    return locationTime < sixHoursAgo;
  };

  // Batch check driver assigned loads for better performance
  const batchCheckDriverAssignedLoads = async (driverIds) => {
    try {
      // If only checking one driver, optimize the query
      /*       
            if (driverIds.length === 1) {
              const { data, error } = await supabase
                .from('loads')
                .select('id, formatted_id, accepted_driver')
                .eq('status', 'assigned')
                .eq('accepted_driver->id', driverIds[0])
                .limit(1)
                .maybeSingle();
      
              if (error) {
                console.error('Error checking driver assigned load:', error);
              }
      
              const loadStatusMap = {};
              loadStatusMap[driverIds[0]] = data
                ? { hasLoad: true, loadDetails: data }
                : { hasLoad: false, loadDetails: null };
      
              return loadStatusMap;
            }
       */
      // Original batch logic for multiple drivers
      const { data, error } = await supabase
        .from('loads')
        .select('id, formatted_id, accepted_driver')
        .eq('status', 'assigned')
        .not('accepted_driver', 'is', null);

      if (error) throw error;

      const loadStatusMap = {};
      driverIds.forEach((driverId) => {
        loadStatusMap[driverId] = { hasLoad: false, loadDetails: null };
      });

      if (data) {
        data.forEach((load) => {
          if (load.accepted_driver && load.accepted_driver.id) {
            const driverId = load.accepted_driver.id;
            if (driverIds.includes(driverId)) {
              loadStatusMap[driverId] = {
                hasLoad: true,
                loadDetails: load,
              };
            }
          }
        });
      }

      return loadStatusMap;
    } catch (error) {
      console.error('Error batch checking driver assigned loads:', error);
      const loadStatusMap = {};
      driverIds.forEach((driverId) => {
        loadStatusMap[driverId] = { hasLoad: false, loadDetails: null };
      });
      return loadStatusMap;
    }
  };

  const getFilterConstraints = async () => {
    let constraints = {
      includeIds: null,
      excludeIds: [],
    };

    if (filters.loadStatus) {
      const { data: loads, error } = await supabase
        .from('loads')
        .select('accepted_driver')
        .eq('status', 'assigned')
        .not('accepted_driver', 'is', null);

      if (!error && loads) {
        const driversWithLoads = loads
          .map(l => l.accepted_driver?.id)
          .filter(Boolean);

        if (filters.loadStatus === 'has_load') {
          constraints.includeIds = driversWithLoads;
        } else if (filters.loadStatus === 'no_load') {
          constraints.excludeIds = [...constraints.excludeIds, ...driversWithLoads];
        }
      }
    }

    return constraints;
  };

  const buildQuery = (constraints = { includeIds: null, excludeIds: [] }) => {

    let selectQuery = '*, last_locations(created_at, user_id)';

    if (filters.locationStatus === 'active' || filters.locationStatus === 'inactive') {
      selectQuery = '*, last_locations!inner(created_at, user_id)';
    }

    let query = supabaseAdmin
      .from('profiles')
      .select(selectQuery, { count: 'exact' })
      .in('user_type', ['driver', 'company_driver']);

    if (constraints.includeIds !== null) {
      if (constraints.includeIds.length === 0) {
        query = query.in('id', ['00000000-0000-0000-0000-000000000000']);
      } else {
        query = query.in('id', constraints.includeIds);
      }
    }

    if (constraints.excludeIds.length > 0) {
      query = query.not('id', 'in', `(${constraints.excludeIds.join(',')})`);
    }

    if (filters.locationStatus) {
      const sixHoursAgo = new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString();

      if (filters.locationStatus === 'active') {
        query = query.gt('last_locations.created_at', sixHoursAgo);
      } else if (filters.locationStatus === 'inactive') {
        query = query.lte('last_locations.created_at', sixHoursAgo);
      } else if (filters.locationStatus === 'no_location') {
        query = query.is('last_locations', null);
      }
    }
    // -------------------------------------

    if (filters.search) {
      const searchTerm = filters.search.trim();
      const searchQuery = `first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,phone_number.ilike.%${searchTerm}%`;
      query = query.or(searchQuery);
    }
    if (filters.vehicleType) query = query.eq('vehicle_type', filters.vehicleType);
    if (filters.status) query = query.eq('status', filters.status);
    if (filters.company) query = query.eq('company_id', filters.company);
    if (filters.towPlate) query = query.ilike('tow_plate', `%${filters.towPlate}%`);
    if (filters.trailerPlate) query = query.ilike('trailer_plate', `%${filters.trailerPlate}%`);
    if (filters.birthDateFrom) query = query.gte('birth_date', filters.birthDateFrom);
    if (filters.birthDateTo) query = query.lte('birth_date', filters.birthDateTo);

    return query;
  };

  const fetchDrivers = async (page = currentPage, resetPage = false, order = sortOrder) => {
    try {
      const loadingState = page === 1 || resetPage ? setLoading : setPageLoading;
      loadingState(true);

      const offset = (page - 1) * driversPerPage;

      const constraints = await getFilterConstraints();

      let countQuery = buildQuery(constraints);
      let dataQuery = buildQuery(constraints)
        .order('first_name', { ascending: order === 'asc' })
        .order('last_name', { ascending: order === 'asc' })
        .range(offset, offset + driversPerPage - 1);

      const [{ count, error: countError }, { data, error: dataError }] =
        await Promise.all([countQuery, dataQuery]);

      if (countError) throw countError;
      if (dataError) throw dataError;

      setTotalFilteredDrivers(count || 0);
      setDrivers(data || []);

      if (page === 1 || resetPage) {
        const { count: totalCount, error: totalCountError } =
          await supabaseAdmin
            .from('profiles')
            .select('*', { count: 'exact', head: true })
            .in('user_type', ['driver', 'company_driver']);

        if (!totalCountError) {
          setTotalDrivers(totalCount || 0);
        }
      }

      if (data && data.length > 0) {
        const driverIds = data.map((driver) => driver.id);

        const loadStatusMap = await batchCheckDriverAssignedLoads(driverIds);

        const latestLocations = {};

        data.forEach(driver => {
          let loc = driver.last_locations;

          if (Array.isArray(loc)) {
            loc = loc.length > 0 ? loc[0] : null;
          }

          if (loc) {
            latestLocations[driver.id] = loc;
          }
        });

        setDriverLocations(latestLocations);
        setDriverLoadStatus(loadStatusMap);
        setError(null);
      } else {
        setDriverLocations({});
        setDriverLoadStatus({});
      }

      setFilteredDrivers(data || []);
    } catch (error) {
      console.error('Error fetching drivers:', error);
      setError('Failed to load drivers');
    } finally {
      setLoading(false);
      setPageLoading(false);
    }
  };



  /* 
    // Optimized function to fetch only the most recent driver
    const fetchLastDriver = async () => {
      try {
        setLoading(true);
  
        // Get only the most recent driver
        const { data, error } = await supabaseAdmin
          .from('profiles')
          .select('*')
          .in('user_type', ['driver', 'company_driver'])
          .order('created_at', { ascending: false })
          .limit(1)
          .single();
  
        if (error) throw error;
  
        if (data) {
          // Fetch location and load status only for this single driver
          const [locationData, loadStatusMap] = await Promise.all([
            // OPTIMAL: Get most recent location timestamp for single driver
            supabase
              .from('locations')
              .select('max(created_at)', 'user_id')
              .eq('user_id', data.id)
              .order('created_at', { ascending: false })
              .limit(1)
              .maybeSingle(),
  
            // Alternative approaches that DON'T work well:
            // ❌ supabase.from('locations').select('max(created_at)').eq('user_id', data.id)
            //    - Only returns timestamp, no location data
            // ❌ supabase.from('locations').select('max(created_at)').in('user_id', [data.id])
            //    - Same issue, just returns max timestamp
  
            // ✅ For multiple drivers, use window functions or separate queries:
            // supabase.from('locations').select('*, row_number() over (partition by user_id order by created_at desc)')
  
            batchCheckDriverAssignedLoads([data.id]),
          ]);
  
          // Set location if exists
          if (locationData.data) {
            setDriverLocations({ [data.id]: locationData.data });
          }
  
          // Set load status
          setDriverLoadStatus(loadStatusMap);
  
          // Set the single driver as the complete list
          setDrivers([data]);
          setFilteredDrivers([data]);
          setTotalFilteredDrivers(1);
          setTotalDrivers(1);
        }
      } catch (error) {
        console.error('Error fetching last driver:', error);
        setError('Failed to load last driver');
      } finally {
        setLoading(false);
      }
    }; */


  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  const openDriverModal = async (driverId) => {
    if (driverId === 'add') {
      setSelectedDriver(null);
    } else {
      // Fetch driver details from Supabase
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', driverId)
        .single();

      if (error) {
        console.error('Error fetching driver:', error);
        return;
      }

      setSelectedDriver(data);
      setTrackingRecord(getTrackingRecord(data.id))
    }
    setIsModalOpen(true);
  };

  const confirmBlockDriver = async (driverId) => {
    try {
      const { error } = await supabaseAdmin
        .from('profiles')
        .update({ status: 'rejected' })
        .eq('id', driverId);

      if (error) throw error;

      // Refresh the drivers list
      await fetchDrivers();
      setError(null);
    } catch (error) {
      console.error('Error approving driver:', error);
      setError('Failed to approve driver');
    }
  };

  const handleSaveDriver = async () => {
    await fetchDrivers(); // Refresh the list after saving
  };

  const handleApproveDriver = async (driverId) => {
    try {
      const { error } = await supabaseAdmin
        .from('profiles')
        .update({ status: 'approved' })
        .eq('id', driverId);

      if (error) throw error;

      // Refresh the drivers list
      await fetchDrivers(currentPage, false, sortOrder);
      setError(null);
    } catch (error) {
      console.error('Error approving driver:', error);
      setError('Failed to approve driver');
    }
  };

  // Update useEffect to handle filtering with server-side pagination
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchDrivers(1, true, sortOrder); // Reset to page 1 when filters change
      setCurrentPage(1);
    }, 300); // Debounce filter changes

    return () => clearTimeout(timeoutId);
  }, [filters, driversPerPage, sortOrder]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Get current drivers - now these are already paginated from server
  const currentDrivers = filteredDrivers;
  const totalPages = Math.ceil(totalFilteredDrivers / driversPerPage);

  const paginate = (pageNumber) => {
    if (
      pageNumber > 0 &&
      pageNumber <= totalPages &&
      pageNumber !== currentPage
    ) {
      setCurrentPage(pageNumber);
      fetchDrivers(pageNumber);
    }
  };

  // Handle change in number of drivers per page
  const handleDriversPerPageChange = (e) => {
    setDriversPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const openAssignCompanyModal = (driverId) => {
    setSelectedDriverForCompany(driverId);
    setIsAssignCompanyModalOpen(true);
  };

  const openNotificationModal = (driver) => {
    setSelectedDriverForNotification(driver);
    setIsNotificationModalOpen(true);
  };

  // Simple encryption function for phone number (you may want to use a more secure method)

  // Send login message function
  const sendLoginMessage = async (driver) => {
    try {
      if (!driver.phone_number) {
        alert('Sürücünün telefon numarası bulunamadı');
        return;
      }

      // Encrypt phone number for the mobile redirect link
      const encryptedPhone = encryptPhoneNumber(driver.phone_number.slice(2));
      const mobileRedirectLink = `https://konvoy.digital/mobile-redirect?token=${encryptedPhone}`;

      const messageBody = {
        chatId: driver.phone_number+"@c.us",
        reply_to: null,
        text: `Merhaba ${driver.first_name} ${driver.last_name}, KONVOY uygulamasına giriş yapmak için aşağıdaki linke tıklayın: ${mobileRedirectLink}`,
        linkPreview: true,
        linkPreviewHighQuality: false,
        session: "default"
      }

      const response = await fetch(
        'https://unowp.vericom.com.tr/api/sendText',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Api-Key': 'c28c71dd752c477b8ce85bc959570d83'
          },
          body: JSON.stringify(messageBody),
        }
      );
      console.log(response);

      if (response.ok) {
        alert(
          `Giriş mesajı ${driver.first_name} ${driver.last_name} adlı sürücüye başarıyla gönderildi`
        );
      } else {
        console.log(response);

        throw new Error('Mesaj gönderme başarısız');
      }
    } catch (error) {
      console.error('Error sending login message:', error);
      alert('Mesaj gönderilirken bir hata oluştu');
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Top Navigation */}
      <header className="bg-white shadow">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button className="text-gray-500 lg:hidden">
                <FontAwesomeIcon icon={faBars} className="text-xl" />
              </button>
            </div>

            <div className="relative">
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 focus:outline-none"
              >
                <span>{user?.email}</span>
                <FontAwesomeIcon icon={faChevronDown} className="text-xs" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100">
        <div className="container mx-auto px-6 py-8">
          <div className="flex items-center justify-between pb-6">
            <div>
              <h1 className="text-2xl font-medium text-gray-800">Sürücüler</h1>
              <p className="text-sm text-gray-500">
                Toplam Sürücü: {totalDrivers}
              </p>
            </div>
            <button
              onClick={() => openDriverModal('add')}
              className="bg-[#4263EB] text-white px-4 py-2 rounded-md hover:bg-[#3b5bd9] flex items-center"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Yeni Sürücü Ekle
            </button>
          </div>

          {/* Filter Section */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sürücü Ara
                </label>
                <input
                  type="text"
                  name="search"
                  placeholder="Ad, Soyad veya ID"
                  value={filters.search}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Yük Durumu
                </label>
                <select
                  name="loadStatus"
                  value={filters.loadStatus}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Tümü</option>
                  <option value="has_load">Yük Atanmış</option>
                  <option value="no_load">Yük Atanmamış</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Konum Durumu
                </label>
                <select
                  name="locationStatus"
                  value={filters.locationStatus}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Tümü</option>
                  <option value="active">Konum Güncel ( &lt; 6 Saat )</option>
                  <option value="inactive">Konum Güncel Değil ( &gt; 6 Saat )</option>
                  <option value="no_location">Konum Bilgisi Yok</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Durum
                </label>
                <select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Tüm Durumlar</option>
                  <option value="pending">Onay Bekliyor</option>
                  <option value="approved">Onaylandı</option>
                  <option value="rejected">Engellendi</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Araç Tipi
                </label>
                <select
                  name="vehicleType"
                  value={filters.vehicleType}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Tüm Tip</option>
                  <option value="closed_box">Closed Box</option>
                  <option value="standart_curtain">Standard Curtain</option>
                  <option value="refrigerated">Refrigerated</option>
                  <option value="adr">ADR</option>
                  <option value="mega_curtain">Mega Curtain</option>
                  <option value="truck&trailer">Truck & Trailer</option>
                  <option value="minivan">Minivan</option>
                  <option value="lorry">Lorry</option>
                  <option value="out_of_gauge">Out of Gauge (OOG)</option>
                  <option value="flatbed">Flatbed</option>
                  <option value="lowbed">Lowbed</option>
                  <option value="jumbo Trailer">Jumbo Trailer</option>
                  <option value="tanker">Tanker</option>
                  <option value="tilt_trailer">Tilt Trailer</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Şirket
                </label>
                <select
                  name="company"
                  value={filters.company}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Tüm Şirketler</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.company_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Çekici Plaka
                </label>
                <input
                  type="text"
                  name="towPlate"
                  placeholder="Ara..."
                  value={filters.towPlate}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dorse Plaka
                </label>
                <input
                  type="text"
                  name="trailerPlate"
                  placeholder="Ara..."
                  value={filters.trailerPlate}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Doğum Tarihi Başlangıç
                  </label>
                  <input
                    type="date"
                    name="birthDateFrom"
                    value={filters.birthDateFrom}
                    onChange={handleFilterChange}
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Doğum Tarihi Bitiş
                  </label>
                  <input
                    type="date"
                    name="birthDateTo"
                    value={filters.birthDateTo}
                    onChange={handleFilterChange}
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Drivers List */}
          <div className="bg-white rounded-lg shadow overflow-hidden relative">
            {error && (
              <div className="p-4 text-red-700 bg-red-100">{error}</div>
            )}

            {loading ? (
              <div className="p-4 text-center">Sürücüler yükleniyor...</div>
            ) : (
              <>
                <div
                  className={`${pageLoading ? 'opacity-50 pointer-events-none' : ''}`}
                >
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                          onClick={toggleSortOrder}>
                          <div className="flex items-center gap-2">
                            Sürücü
                            <FontAwesomeIcon
                              icon={sortOrder === 'asc' ? faSortUp : faSortDown}
                              className="text-gray-400"
                            />
                          </div>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          İletişim
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Araç
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Şirket
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Yük Durumu
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Konum
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Eylemler
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {currentDrivers.map((driver) => (
                        <tr
                          key={driver.id}
                          className={`${driver.user_type === 'company_driver' ? 'bg-gray-50' : 'bg-white'}`}
                        >
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <div>
                                <div className="font-medium text-gray-900">
                                  {driver.first_name} {driver.last_name}
                                </div>
                                {/* <div className="text-sm text-gray-500">
                                  ID: {driver.id || 'N/A'}
                                </div> */}
                                <div className="text-xs text-gray-400">
                                  Kayıt Tarihi:{' '}
                                  {new Date(
                                    driver.created_at
                                  ).toLocaleDateString('tr-TR', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                  })}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {driver.phone_number || 'N/A'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {driver.email || 'N/A'}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              {driver.vehicle_type || 'Not specified'}
                            </div>
                            <div className="text-sm text-gray-500">
                              Çekici: {driver.tow_plate || 'N/A'}
                            </div>
                            <div className="text-sm text-gray-500">
                              Dorse: {driver.trailer_plate || 'N/A'}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            {driver.user_type === 'company_driver' ? (
                              <div className="text-sm text-gray-900">
                                {driver.company || 'N/A'}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-500">
                                Bağımsız Sürücü
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            {driverLoadStatus[driver.id]?.hasLoad ? (
                              <div>
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                  Yük Atanmış
                                </span>
                                <div className="text-xs text-gray-500 mt-1">
                                  {driverLoadStatus[driver.id]?.loadDetails
                                    ?.formatted_id || 'Yük ID: N/A'}
                                </div>
                              </div>
                            ) : (
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                                Yük Atanmamış
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            {isSharingLocation(driver.id) && (
                              <FontAwesomeIcon
                                icon={faLocationArrow}
                                className="mr-1 text-green-500"
                              />
                            )
                            }
                            {driverLocations[driver.id] ? (
                              <div className="flex items-center">
                                <FontAwesomeIcon
                                  icon={faMapMarkerAlt}
                                  className={`mr-1 ${isLocationOld(driverLocations[driver.id].created_at) ? 'text-red-500' : 'text-green-500'}`}
                                />
                                <div className="text-xs">
                                  {new Date(
                                    driverLocations[driver.id].created_at
                                  ).toLocaleString('tr-TR', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: false,
                                  })}
                                  {isLocationOld(
                                    driverLocations[driver.id].created_at
                                  ) && (
                                      <div className="flex items-center text-red-500 mt-1">
                                        <FontAwesomeIcon
                                          icon={faExclamationTriangle}
                                          className="mr-1"
                                        />
                                        <span>6 saat güncel değil</span>
                                      </div>
                                    )}
                                </div>
                              </div>
                            ) : (
                              <span className="text-gray-500 text-xs">
                                Konum bilgisi yok
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <button
                              onClick={() => openDriverModal(driver.id)}
                              className="text-blue-600 hover:text-blue-900 mr-3"
                            >
                              <FontAwesomeIcon icon={faEye} className="mr-1" />
                              Detaylar
                            </button>
                            {driver.status !== 'approved' && (
                              <button
                                onClick={() => handleApproveDriver(driver.id)}
                                className="text-green-600 hover:text-green-900 mr-3"
                              >
                                <FontAwesomeIcon
                                  icon={faCheck}
                                  className="mr-1"
                                />
                                Onayla
                              </button>
                            )}
                            {driver.user_type === 'driver' && (
                              <button
                                onClick={() =>
                                  openAssignCompanyModal(driver.id)
                                }
                                className="text-blue-600 hover:text-blue-900 mr-3"
                              >
                                <FontAwesomeIcon
                                  icon={faBuilding}
                                  className="mr-1"
                                />
                                Firma Ata
                              </button>
                            )}
                            <button
                              onClick={() => confirmBlockDriver(driver.id)}
                              className="text-red-600 hover:text-red-900 mr-3"
                            >
                              <FontAwesomeIcon icon={faBan} className="mr-1" />
                              Engelle
                            </button>
                            <button
                              onClick={() => openNotificationModal(driver)}
                              className="text-blue-600 hover:text-blue-900 mr-3"
                            >
                              <FontAwesomeIcon icon={faBell} className="mr-1" />
                              Bildirim Gönder
                            </button>
                            <button
                              onClick={() => sendLoginMessage(driver)}
                              className="text-green-600 hover:text-green-900"
                            >
                              <FontAwesomeIcon
                                icon={faMobileAlt}
                                className="mr-1"
                              />
                              Giriş Mesajı Gönder
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {pageLoading && (
                  <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                      <p className="mt-2 text-sm text-gray-600">
                        Sayfa yükleniyor...
                      </p>
                    </div>
                  </div>
                )}

                {/* Pagination */}
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex flex-col space-y-3 sm:hidden">
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1 || pageLoading}
                        className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${currentPage === 1 || pageLoading
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                      >
                        Önceki
                      </button>
                      <div className="flex items-center">
                        <span className="text-sm text-gray-700 mr-2">
                          Sayfa başına:
                        </span>
                        <select
                          value={driversPerPage}
                          onChange={handleDriversPerPageChange}
                          disabled={pageLoading}
                          className="border border-gray-300 rounded-md text-sm p-1 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                        >
                          <option value="5">5</option>
                          <option value="10">10</option>
                          <option value="25">25</option>
                          <option value="50">50</option>
                          <option value="100">100</option>
                        </select>
                      </div>
                      <button
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages || pageLoading}
                        className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${currentPage === totalPages || pageLoading
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                      >
                        Sonraki
                      </button>
                    </div>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div className="flex items-center">
                      <p className="text-sm text-gray-700 mr-4">
                        Görüntülenen{' '}
                        <span className="font-medium">
                          {(currentPage - 1) * driversPerPage + 1}
                        </span>{' '}
                        -{' '}
                        <span className="font-medium">
                          {Math.min(
                            currentPage * driversPerPage,
                            totalFilteredDrivers
                          )}
                        </span>{' '}
                        of{' '}
                        <span className="font-medium">
                          {totalFilteredDrivers}
                        </span>{' '}
                        sonuç
                      </p>
                      <div className="flex items-center">
                        <span className="text-sm text-gray-700 mr-2">
                          Sayfa başına:
                        </span>
                        <select
                          value={driversPerPage}
                          onChange={handleDriversPerPageChange}
                          disabled={pageLoading}
                          className="border border-gray-300 rounded-md text-sm p-1 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                        >
                          <option value="5">5</option>
                          <option value="10">10</option>
                          <option value="25">25</option>
                          <option value="50">50</option>
                          <option value="100">100</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <nav
                        className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                        aria-label="Pagination"
                      >
                        <button
                          onClick={() => paginate(currentPage - 1)}
                          disabled={currentPage === 1 || pageLoading}
                          className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 text-sm font-medium ${currentPage === 1 || pageLoading
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-white text-gray-500 hover:bg-gray-50'
                            }`}
                        >
                          <FontAwesomeIcon
                            icon={faChevronLeft}
                            className="h-4 w-4"
                          />
                        </button>

                        {/* Page Numbers */}
                        {[...Array(totalPages)].map((_, index) => {
                          const pageNumber = index + 1;
                          // Show current page, first page, last page, and one page before and after current
                          if (
                            pageNumber === 1 ||
                            pageNumber === totalPages ||
                            (pageNumber >= currentPage - 1 &&
                              pageNumber <= currentPage + 1)
                          ) {
                            return (
                              <button
                                key={pageNumber}
                                onClick={() => paginate(pageNumber)}
                                disabled={pageLoading}
                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === pageNumber
                                    ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                    : pageLoading
                                      ? 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed'
                                      : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                  }`}
                              >
                                {pageNumber}
                              </button>
                            );
                          }
                          // Show dots if there's a gap
                          if (
                            pageNumber === currentPage - 2 ||
                            pageNumber === currentPage + 2
                          ) {
                            return (
                              <span
                                key={pageNumber}
                                className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-gray-700"
                              >
                                ...
                              </span>
                            );
                          }
                          return null;
                        })}

                        <button
                          onClick={() => paginate(currentPage + 1)}
                          disabled={currentPage === totalPages || pageLoading}
                          className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 text-sm font-medium ${currentPage === totalPages || pageLoading
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-white text-gray-500 hover:bg-gray-50'
                            }`}
                        >
                          <FontAwesomeIcon
                            icon={faChevronRight}
                            className="h-4 w-4"
                          />
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      {/* Add the modal */}
      <DriverModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        driver={selectedDriver}
        trackingRecord={trackingRecord}
        onSave={handleSaveDriver}
      />

      {/* Add AssignCompanyModal */}
      <AssignCompanyModal
        isOpen={isAssignCompanyModalOpen}
        onClose={() => setIsAssignCompanyModalOpen(false)}
        driverId={selectedDriverForCompany}
        onAssign={fetchDrivers}
      />

      {/* Add NotificationModal */}
      <DriverNotificationModal
        isOpen={isNotificationModalOpen}
        onClose={() => setIsNotificationModalOpen(false)}
        driver={selectedDriverForNotification}
      />
    </div>
  );
}

export default Drivers;